import { BaseDropdown, BaseInput, LargeButton, Ptag, SuccessModal } from "components/core";
import { FieldArray, Form, Formik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { colors } from "theme";
import DependentsCardList from "./DependentsCardList";
import GradeAccordion from "./GradeAccordion";
import GradeRoomLimit from "./GradeRoomLimit";
import GradeMaternity from "./GradeMaternity";
import GradeDiseaseWise from "./GradeDiseaseWise";
import "./styles.css";
import GradeCoPayment from "./GradeCoPayment";
import GradeSumInsured from "./GradeSumInsured";
import GradeDynamic from "./GradeDynamic";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import {
  ageLimitOptions,
  generateGradeInitials,
  maxChildrenDropdownOptions,
  saveGrade,
  validationSchema,
} from "./schema";
import { formatIndianNumber } from "utlis/utils";
import BaseTextArea from "components/BaseTextArea";
import Alert from "assets/svgs/report.svg";
import { Button } from "@mui/material";

const GradeView = ({
  attributes,
  grade,
  isFlat,
  isPostIssuance,
  interstitialGradeAddition,
  refreshGradesData,
}) => {
  const userplanId = localStorage.getItem("eb_current_plan");
  let selectedDetails = localStorage.getItem("eb_selected_values");
  if (selectedDetails) {
    selectedDetails = JSON.parse(selectedDetails);
  }
  const [selectedDependents, setSelectedDependents] = useState([]);
  const [toggleStatus, setToggleStatus] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [maternityCoverageEnabled, setMaternityCoverageEnabled] = useState(false);
  
  // Check if this is a plan type that should have Employee selected by default and disabled
  const isEmployeeDefaultPlan = useMemo(() => {
    if (!selectedDetails || !userplanId) return false;
    const productType = selectedDetails.userplans_data[userplanId]?.product_type?.product_type_name;
    const isDefaultPlan = [
      "health_insurance",        // Group Medical Cover
      "personal_accident",       // Group Personal Accident
      "term_life",              // Group Term Life
    ].includes(productType);
    
    
    return isDefaultPlan;
  }, [selectedDetails, userplanId]);

  // Check if this is a plan type that should have Parents selected by default and disabled
  const isParentsDefaultPlan = useMemo(() => {
    if (!selectedDetails || !userplanId) return false;
    const productType = selectedDetails.userplans_data[userplanId]?.product_type?.product_type_name;
    const isDefaultPlan = [
      "parental_cover",         // Group Parental Cover
    ].includes(productType);
    
    
    return isDefaultPlan;
  }, [selectedDetails, userplanId]);
  
  const handleClick = (name) => {
    // Don't allow deselecting Employee for specific plan types
    if (isEmployeeDefaultPlan && name === "Employee") {
      return;
    }
    
    // Don't allow deselecting Parents for specific plan types
    if (isParentsDefaultPlan && name === "Parents") {
      return;
    }
    
    const newList = new Set(selectedDependents);
    if (newList.has(name)) {
      newList.delete(name);
    } else {
      newList.add(name);
    }
    
    // Ensure Employee is always included for specific plan types
    if (isEmployeeDefaultPlan) {
      newList.add("Employee");
    }
    
    // Ensure Parents is always included for specific plan types
    if (isParentsDefaultPlan) {
      newList.add("Parents");
    }
    
    setSelectedDependents([...newList]);
  };

  useEffect(() => {
    if (grade.duplicate_data) {
      // Use selected_dependents if available, otherwise fall back to dependents_allowed
      const dependents = grade.selected_dependents || grade?.dependents_covered?.dependents_allowed || [];
      let finalDependents = [...dependents];
      
      // Ensure Employee is always included for specific plan types
      if (isEmployeeDefaultPlan && !finalDependents.includes("Employee")) {
        finalDependents.push("Employee");
      }
      
      // Ensure Parents is always included for specific plan types
      if (isParentsDefaultPlan && !finalDependents.includes("Parents")) {
        finalDependents.push("Parents");
      }
      
      setSelectedDependents(finalDependents);
      return;
    }
    if (grade?.dependents_covered?.dependents_allowed.length) {
      const dependents = grade?.dependents_covered?.dependents_allowed;
      let finalDependents = [...dependents];
      
      // Ensure Employee is always included for specific plan types
      if (isEmployeeDefaultPlan && !finalDependents.includes("Employee")) {
        finalDependents.push("Employee");
      }
      
      // Ensure Parents is always included for specific plan types
      if (isParentsDefaultPlan && !finalDependents.includes("Parents")) {
        finalDependents.push("Parents");
      }
      
      setSelectedDependents(finalDependents);
    } else if (isEmployeeDefaultPlan) {
      // For specific plan types, set Employee as default
      setSelectedDependents(["Employee"]);
    } else if (isParentsDefaultPlan) {
      // For specific plan types, set Parents as default
      setSelectedDependents(["Parents"]);
    }
  }, [grade, isEmployeeDefaultPlan, isParentsDefaultPlan]);

  const getAttribute = (name) => {
    return attributes.find(
      (attribute) => attribute.attribute_data.attribute_name === name
    );
  };

  // Unified validation function to ensure consistency between handleSubmit and hasRequiredFieldErrors
  const validateToggleFields = (values, setFieldError = null) => {
    try {
      // Maximum safety check for function parameters
      if (!values || typeof values !== 'object') {
        return false;
      }
      
      if (!toggleStatus || typeof toggleStatus !== 'object') {
        return false;
      }
      
      const hasActiveToggles = Object.values(toggleStatus).some(status => status === true);
      let hasIncompleteFields = false;
      
      console.log("validateToggleFields - hasActiveToggles:", hasActiveToggles);
      console.log("validateToggleFields - toggleStatus:", toggleStatus);
    
    if (hasActiveToggles) {
      // Check co-payment fields if co-payment toggle is on
      if (toggleStatus["Co Payment"]) {
        const hasEmployeeSpouseChildren = selectedDependents.some(dep => 
          ["Employee", "Spouse", "Children"].includes(dep)
        );
        
        if (hasEmployeeSpouseChildren && (!values.esc_limit || values.esc_limit.trim() === "")) {
          if (setFieldError) {
            setFieldError("esc_limit", "*Required");
          }
          hasIncompleteFields = true;
        }
        
        const hasParents = selectedDependents.some(dep => 
          ["Parents", "In-laws"].includes(dep)
        );
        
        if (hasParents && (!values.parent_limit || values.parent_limit.trim() === "")) {
          if (setFieldError) {
            setFieldError("parent_limit", "*Required");
          }
          hasIncompleteFields = true;
        }
      }
      
      // Check if dependents are selected (only for non-co-payment toggles)
      const nonCoPaymentToggles = Object.keys(toggleStatus).filter(key => 
        key !== "Co Payment" && toggleStatus[key]
      );
      
      if (nonCoPaymentToggles.length > 0 && selectedDependents.length === 0) {
        if (setFieldError) {
          setFieldError("dependents_covered", "Please select at least one dependent");
        }
        hasIncompleteFields = true;
      }
      
      // Check ambulance fields if ambulance toggle is on
      if (toggleStatus["Ambulance Charges"]) {
        if (!values.ambulance_limit || values.ambulance_limit.trim() === "") {
          if (setFieldError) {
            setFieldError("ambulance_limit", "*Required");
          }
          hasIncompleteFields = true;
        }
      }
      
      // Check OPD fields if OPD toggle is on
      if (toggleStatus["OPD Charges"]) {
        if (!values.opd_limit || values.opd_limit.trim() === "") {
          if (setFieldError) {
            setFieldError("opd_limit", "*Required");
          }
          hasIncompleteFields = true;
        }
      }
      
      // Check health checkup fields if health checkup toggle is on
      if (toggleStatus["Health Checkup Coverage"]) {
        if (!values.health_checkup_limit || values.health_checkup_limit.trim() === "") {
          if (setFieldError) {
            setFieldError("health_checkup_limit", "*Required");
          }
          hasIncompleteFields = true;
        }
      }
      
      // Check dental fields if dental toggle is on
      if (toggleStatus["Dental Coverage"]) {
        if (!values.dental_limit || values.dental_limit.trim() === "") {
          if (setFieldError) {
            setFieldError("dental_limit", "*Required");
          }
          hasIncompleteFields = true;
        }
      }
      
      // Check critical illness fields if critical illness toggle is on
      if (toggleStatus["Critical Illness Coverages"]) {
        if (!values.critical_illness_limit || values.critical_illness_limit.trim() === "") {
          if (setFieldError) {
            setFieldError("critical_illness_limit", "*Required");
          }
          hasIncompleteFields = true;
        }
      }
      
      // Check AYUSH Treatment fields if AYUSH Treatment toggle is on
      if (toggleStatus["AYUSH Treatment"]) {
        if (!values.ayush_treatment_limit || values.ayush_treatment_limit.trim() === "") {
          if (setFieldError) {
            setFieldError("ayush_treatment_limit", "*Required");
          }
          hasIncompleteFields = true;
        }
      }
      
      // Check congenital internal disease fields if congenital internal disease toggle is on
      if (toggleStatus["Congenital Internal Disease Coverage"]) {
        if (!values.congenital_internal_limit || values.congenital_internal_limit.trim() === "") {
          if (setFieldError) {
            setFieldError("congenital_internal_limit", "*Required");
          }
          hasIncompleteFields = true;
        }
      }
      
      // Check congenital external disease fields if congenital external disease toggle is on
      if (toggleStatus["Congenital External Disease Coverage"]) {
        if (!values.congenital_external_limit || values.congenital_external_limit.trim() === "") {
          if (setFieldError) {
            setFieldError("congenital_external_limit", "*Required");
          }
          hasIncompleteFields = true;
        }
      }
      
      // Check room rent limit fields if room rent limit toggle is on
      if (toggleStatus["Room Rent Limit"]) {
        const isMetroTogglerOn = values.room_rent_metro_toggler;
        
        if (isMetroTogglerOn) {
          // If metro/non-metro toggle is on, at least one field from each category must be filled
          
          // Check Normal Room fields - at least one must be filled
          const normalRoomFields = [
            "non_metro_room_rent_limit_normal", 
            "non_metro_room_rent_limit_normal_max"
          ];
          const hasNormalRoomValue = normalRoomFields.some(field => 
            values[field] && values[field].toString().trim() !== ""
          );
          
          if (!hasNormalRoomValue) {
            if (setFieldError) {
              setFieldError("non_metro_room_rent_limit_normal", "*Required");
            }
            hasIncompleteFields = true;
          }
          
          // Check ICU Room fields - at least one must be filled
          const icuRoomFields = [
            "non_metro_room_rent_limit_icu", 
            "non_metro_room_rent_limit_icu_max"
          ];
          const hasIcuRoomValue = icuRoomFields.some(field => 
            values[field] && values[field].toString().trim() !== ""
          );
          
          if (!hasIcuRoomValue) {
            if (setFieldError) {
              setFieldError("non_metro_room_rent_limit_icu", "*Required");
            }
            hasIncompleteFields = true;
          }
        } else {
          // If metro/non-metro toggle is off, check fields based on what's rendered
          const roomRentAttribute = attributes.find(attr => 
            attr.attribute_data.attribute_name === "Room Rent Limit"
          );
          const doesRangeExist = roomRentAttribute?.selectable_values?.length > 0;
          const isFlex = selectedDetails?.flex;
          
          if (doesRangeExist) {
            // When doesRangeExist is true, only check room_rent_limit and optionally min/max
            if (!values.room_rent_limit || values.room_rent_limit.toString().trim() === "") {
              if (setFieldError) {
                setFieldError("room_rent_limit", "*Required");
              }
              hasIncompleteFields = true;
            }
            
            // Only check min/max if they are rendered (doesRangeExist && isFlex)
            if (isFlex) {
              if (!values.room_rent_limit_min || values.room_rent_limit_min.toString().trim() === "") {
                if (setFieldError) {
                  setFieldError("room_rent_limit_min", "*Required");
                }
                hasIncompleteFields = true;
              }
              if (!values.room_rent_limit_max || values.room_rent_limit_max.toString().trim() === "") {
                if (setFieldError) {
                  setFieldError("room_rent_limit_max", "*Required");
                }
                hasIncompleteFields = true;
              }
            }
          } else {
            // When doesRangeExist is false, at least one field from each category must be filled
            
            // Check Normal Room fields - at least one must be filled
            const normalRoomFields = [
              "default_metro_room_rent_limit_normal",
              "default_metro_room_rent_limit_normal_max"
            ];
            const hasNormalRoomValue = normalRoomFields.some(field => 
              values[field] && values[field].toString().trim() !== ""
            );
            
            if (!hasNormalRoomValue) {
              if (setFieldError) {
                setFieldError("default_metro_room_rent_limit_normal", "*Required");
              }
              hasIncompleteFields = true;
            }
            
            // Check ICU Room fields - at least one must be filled
            const icuRoomFields = [
              "default_metro_room_rent_limit_icu",
              "default_metro_room_rent_limit_icu_max"
            ];
            const hasIcuRoomValue = icuRoomFields.some(field => 
              values[field] && values[field].toString().trim() !== ""
            );
            
            if (!hasIcuRoomValue) {
              if (setFieldError) {
                setFieldError("default_metro_room_rent_limit_icu", "*Required");
              }
              hasIncompleteFields = true;
            }
          }
        }
      }
      
      // Check maternity fields if maternity toggle is on
      if (toggleStatus["Maternity Coverage"]) {
        console.log("validateToggleFields - Maternity Coverage is enabled");
        console.log("validateToggleFields - toggleStatus:", toggleStatus);
        const isMetroTogglerOn = values.maternity_metro_toggler;
        console.log("validateToggleFields - isMetroTogglerOn:", isMetroTogglerOn);
        
        if (isMetroTogglerOn) {
          // Simple validation - just check if any metro/non-metro fields are empty
          const metroFields = [
            "metro_maternity_delivery_coverage_limit_normal",
            "metro_maternity_delivery_coverage_limit_caeserian"
          ];
          
          const nonMetroFields = [
            "non_metro_maternity_delivery_coverage_limit_normal",
            "non_metro_maternity_delivery_coverage_limit_caeserian"
          ];
          
          // Check if any metro fields are empty
          const hasEmptyMetroFields = metroFields.some(field => {
            const value = values[field];
            return !value || (typeof value === 'string' && value.trim() === '');
          });
          
          // Check if any non-metro fields are empty
          const hasEmptyNonMetroFields = nonMetroFields.some(field => {
            const value = values[field];
            return !value || (typeof value === 'string' && value.trim() === '');
          });
          
          console.log("Metro/Non-Metro validation:", {
            hasEmptyMetroFields,
            hasEmptyNonMetroFields,
            metroValues: metroFields.map(field => ({ field, value: values[field] })),
            nonMetroValues: nonMetroFields.map(field => ({ field, value: values[field] }))
          });
          
          // If any fields are empty, show the attention banner (don't set individual field errors)
          if (hasEmptyMetroFields || hasEmptyNonMetroFields) {
            hasIncompleteFields = true;
            console.log("Metro/Non-Metro fields are empty - will show attention banner");
          }
        } else {
          // Simple validation for default mode - just check if fields are empty
          console.log("validateToggleFields - Using default mode validation");
          const defaultFields = [
            "maternity_delivery_coverage_limit_normal_max",
            "maternity_delivery_coverage_limit_icu_max"
          ];
          
          // Check if any default fields are empty
          const hasEmptyDefaultFields = defaultFields.some(field => {
            const value = values[field];
            return !value || (typeof value === 'string' && value.trim() === '');
          });
          
          console.log("Default maternity validation:", {
            hasEmptyDefaultFields,
            fieldValues: defaultFields.map(field => ({ field, value: values[field] }))
          });
          
          // If any fields are empty, show the attention banner
          if (hasEmptyDefaultFields) {
            hasIncompleteFields = true;
            console.log("Default maternity fields are empty - will show attention banner");
          }
        }
      }
      
      // Check disease wise sub limit fields if disease wise sub limit toggle is on
      if (toggleStatus["Disease Wise Sub limit"]) {
        const diseaseWiseDetails = values.disease_wise_limit_detail || [];
        
        // Check if at least one disease is added
        if (diseaseWiseDetails.length === 0) {
          if (setFieldError) {
            setFieldError("disease_wise_limit_detail", "Please add at least one disease");
          }
          hasIncompleteFields = true;
        } else {
          // Check each disease entry for disease name and limit
          diseaseWiseDetails.forEach((disease, index) => {
            if (!disease || !disease.disease_name || (typeof disease.disease_name === "string" && disease.disease_name.trim() === "")) {
              if (setFieldError) {
                setFieldError(`disease_wise_limit_detail[${index}]disease_name`, "*Required");
              }
              hasIncompleteFields = true;
            }
            if (!disease || disease.disease_limit === undefined || disease.disease_limit === null || (typeof disease.disease_limit === "string" && disease.disease_limit.trim() === "")) {
              if (setFieldError) {
                setFieldError(`disease_wise_limit_detail[${index}]disease_limit`, "*Required");
              }
              hasIncompleteFields = true;
            }
          });
        }
      }
      
    } else {
      console.log("validateToggleFields - No active toggles, returning false");
      return false;
    }
    
    console.log("validateToggleFields - Final result:", {
      hasIncompleteFields,
      toggleStatus,
      maternityToggleStatus: toggleStatus["Maternity Coverage"],
      allToggleStatus: Object.keys(toggleStatus).filter(key => toggleStatus[key])
    });
    
    return hasIncompleteFields;
    
    } catch (error) {
      console.error("Critical error in validateToggleFields:", error);
      console.error("Error stack:", error.stack);
      return true; // Return true to indicate validation failed
    }
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    
    // Console log formik errors for debugging
    console.log("GradeView handleSubmit - values:", values);
    console.log("GradeView handleSubmit - toggleStatus:", toggleStatus);
    console.log("GradeView handleSubmit - maternityCoverageEnabled:", maternityCoverageEnabled);
    
    // Check if maternity coverage is enabled and has empty fields
    const isMaternityEnabled = toggleStatus["Maternity Coverage"];
    let hasEmptyMaternityFields = false;
    
    if (isMaternityEnabled) {
      const isMetroTogglerOn = values.maternity_metro_toggler;
      
      if (isMetroTogglerOn) {
        // Check metro/non-metro fields
        const metroFields = [
          "metro_maternity_delivery_coverage_limit_normal",
          "metro_maternity_delivery_coverage_limit_caeserian"
        ];
        const nonMetroFields = [
          "non_metro_maternity_delivery_coverage_limit_normal",
          "non_metro_maternity_delivery_coverage_limit_caeserian"
        ];
        
        hasEmptyMaternityFields = [...metroFields, ...nonMetroFields].some(field => {
          const value = values[field];
          return !value || (typeof value === 'string' && value.trim() === '');
        });
      } else {
        // Check default fields
        const defaultFields = [
          "maternity_delivery_coverage_limit_normal_max",
          "maternity_delivery_coverage_limit_icu_max"
        ];
        
        hasEmptyMaternityFields = defaultFields.some(field => {
          const value = values[field];
          return !value || (typeof value === 'string' && value.trim() === '');
        });
      }
    }
    
    console.log("GradeView handleSubmit - hasEmptyMaternityFields:", hasEmptyMaternityFields);
    
   
    const hasIncompleteFields = validateToggleFields(values, setFieldError);
    
    console.log("GradeView handleSubmit - hasIncompleteFields:", hasIncompleteFields);
    console.log("GradeView handleSubmit - isMaternityEnabled:", isMaternityEnabled);
    console.log("GradeView handleSubmit - hasEmptyMaternityFields:", hasEmptyMaternityFields);
    
    if (hasIncompleteFields) {
      console.log("GradeView handleSubmit - Blocking save due to incomplete fields");
      setSubmitting(false);
      return;
    }
    
    if (
      [
        "health_insurance",
        "health_insurance_additional",
        "opd_cover",
        "top_up_health",
        "parental_cover",
        "health_insurance_parents",
        "health_insurance_parents_in_laws",
      ].includes(productType) &&
      !selectedDependents?.length
    )
      return;
    
    try {
      console.log("handleSubmit - Before saveGrade:", {
        isFlat,
        planType: productType,
        currentValues: values,
        sumInsured: values.sum_insured,
        sumInsuredMin: values.sum_insured_min,
        sumInsuredMax: values.sum_insured_max,
        escLimit: values.esc_limit,
        parentLimit: values.parent_limit
      });
      
      const saveResult = await saveGrade(
        values,
        userplanId,
        selectedDependents,
        attributes,
        grade,
        toggleStatus,
        isFlat
      );
      
      console.log("handleSubmit - saveGrade result:", saveResult);
      setShowSuccessModal(true);
      
      // Reset maternity coverage enabled state after successful save
      setMaternityCoverageEnabled(false);
      
       if (refreshGradesData) {
         console.log("handleSubmit - Calling refreshGradesData for", isFlat ? "FLAT" : "GRADED", "plan");
         console.log("handleSubmit - Grade data BEFORE refresh:", {
           grade_code: grade.grade_code,
           grade_attributes_data: grade.grade_attributes_data,
           sum_insured: grade.grade_attributes_data?.find(attr => 
             attr.attribute_data?.attribute_name === "Sum Insured"
           ),
           co_payment: grade.grade_attributes_data?.find(attr => 
             attr.attribute_data?.attribute_name === "Co Payment"
           ),
           room_rent: grade.grade_attributes_data?.find(attr => 
             attr.attribute_data?.attribute_name === "Room Rent Limit"
           )
         });
         await refreshGradesData();
         console.log("handleSubmit - refreshGradesData completed");
         
         // Add a small delay to ensure the grade data is updated
         await new Promise(resolve => setTimeout(resolve, 100));
         
         console.log("handleSubmit - Grade data AFTER refresh:", {
           grade_code: grade.grade_code,
           grade_attributes_data: grade.grade_attributes_data,
           sum_insured: grade.grade_attributes_data?.find(attr => 
             attr.attribute_data?.attribute_name === "Sum Insured"
           ),
           co_payment: grade.grade_attributes_data?.find(attr => 
             attr.attribute_data?.attribute_name === "Co Payment"
           ),
           room_rent: grade.grade_attributes_data?.find(attr => 
             attr.attribute_data?.attribute_name === "Room Rent Limit"
           )
         });
       }
    } catch (error) {
      console.error("handleSubmit - Error saving grade:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const productType = useMemo(() => {
    return selectedDetails.userplans_data[userplanId].product_type
      .product_type_name;
  }, [selectedDetails, userplanId]);

  const initialValues = useMemo(() => {
    console.log("initialValues useMemo called with:", {
      isFlat,
      planType: productType,
      grade,
      attributes,
      duplicate_data: grade.duplicate_data,
      grade_attributes_data: grade.grade_attributes_data,
      sum_insured_in_grade: grade?.grade_attributes_data?.find(attr => 
        attr.attribute_data?.attribute_name === "Sum Insured"
      ),
      co_payment_in_grade: grade?.grade_attributes_data?.find(attr => 
        attr.attribute_data?.attribute_name === "Co Payment"
      ),
      room_rent_in_grade: grade?.grade_attributes_data?.find(attr => 
        attr.attribute_data?.attribute_name === "Room Rent Limit"
      )
    });

    if (grade.duplicate_data) {
      // Ensure duplicate data has all the same structure as generated initials
      const generatedInitials = generateGradeInitials(attributes, grade);
      
      // Extract Sum Insured values from the source grade's grade_attributes_data
      const sumInsuredAttribute = grade.grade_attributes_data?.find(
        attr => {
          const attributeId = attributes.find(
            (attr) => attr.attribute_data.attribute_name === "Sum Insured"
          )?.attribute_product_mapping_id;
          return attr.attribute_product_mapping === attributeId;
        }
      );
      
      const duplicateData = {
        ...generatedInitials,
        ...grade, // Override with the duplicate data
        duplicate_data: true, // Preserve the duplicate flag
      };
      
      // Override Sum Insured fields with values from source grade
      if (sumInsuredAttribute) {
        duplicateData.sum_insured = formatIndianNumber(sumInsuredAttribute.attribute_default);
        duplicateData.sum_insured_min = formatIndianNumber(sumInsuredAttribute.attribute_min);
        duplicateData.sum_insured_max = formatIndianNumber(sumInsuredAttribute.attribute_max);
      }
      
      console.log("initialValues - duplicateData result:", duplicateData);
      return duplicateData;
    }
    
      const generatedInitials = generateGradeInitials(attributes, grade);
      console.log("initialValues - generatedInitials result:", {
        sum_insured: generatedInitials.sum_insured,
        sum_insured_min: generatedInitials.sum_insured_min,
        sum_insured_max: generatedInitials.sum_insured_max,
        esc_limit: generatedInitials.esc_limit,
        parent_limit: generatedInitials.parent_limit,
        room_rent_limit: generatedInitials.room_rent_limit,
        maternity_delivery_coverage_limit_normal_max: generatedInitials.maternity_delivery_coverage_limit_normal_max,
        maternity_delivery_coverage_limit_icu_max: generatedInitials.maternity_delivery_coverage_limit_icu_max
      });
      return generatedInitials;
  }, [attributes, grade, isFlat, productType]);

  const toggleHandler = (bool, name) => {
    setToggleStatus((prev) => ({
      ...prev,
      [name]: bool,
    }));
  };

  const handleMaternityToggleChange = (isEnabled) => {
    setMaternityCoverageEnabled(isEnabled);
  };
  const validations = useMemo(() => {
    return validationSchema(attributes, grade, isPostIssuance, selectedDependents);
  }, [attributes, grade, isPostIssuance, selectedDependents]);

  const showParents = useMemo(() => {
    return (
      selectedDependents.includes("Parents") ||
      selectedDependents.includes("In-laws")
    );
  }, [selectedDependents]);

  const showChildren = useMemo(() => {
    return selectedDependents.includes("Children");
  }, [selectedDependents]);





  const handleDuplicateGradeClick = (event, values) => {
    event.preventDefault();
    values.selected_dependents = selectedDependents;
    // Keep the min/max values from the duplicated grade instead of setting them to null
    // values.sum_insured_max = null;
    // values.sum_insured_min = null;
    interstitialGradeAddition(values);
  };

  return (
    <>
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message="Selections Saved!"
      />
      <section
        className={isFlat?"grade-view-containers":"grade-view-container"}
        style={{
          border: isFlat ? "1px solid black" : "1px solid black",
          padding: isFlat ? "24px" : "24px",
          background: "white",
        }}
      >
      <Formik
        key={`${grade.grade_code}-${grade.grade_attributes_data?.length || 0}`}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validations}
        onSubmit={handleSubmit}
        validateOnBlur={true}
        validateOnChange={true}
      >
        {(formikProps) => {
          console.log("Formik render - current values for", isFlat ? "FLAT" : "GRADED", "plan:", {
            isFlat,
            planType: productType,
            sum_insured: formikProps.values.sum_insured,
            sum_insured_min: formikProps.values.sum_insured_min,
            sum_insured_max: formikProps.values.sum_insured_max,
            esc_limit: formikProps.values.esc_limit,
            parent_limit: formikProps.values.parent_limit
          });
          
          return (
            <Form style={{}}>
              {false && (
                <div
                  style={{
                    width: "50%",
                    minWidth: "400px",
                  }}
                >
                  <Ptag
                    color={colors.primaryColor}
                    fontSize="18px"
                    fontWeight="bold"
                    marginBottom="10px"
                  >
                    Grade Code
                  </Ptag>
                  <BaseInput
                    type="text"
                    fontSize="16px !important"
                    formikProps={{ ...formikProps, name: "grade_code" }}
                  />
                </div>
              )}
              {(() => {
                // Check if there are required field errors (empty fields) vs validation errors (min/max)
                const hasRequiredFieldErrors = () => {
                  const errors = formikProps?.errors || {};
                  const values = formikProps?.values || {};
                  
                  
                  // Check for empty required fields by accessing nested values directly
                  const maxChildren = values?.dependents_covered?.max_number_children;
                  const childrenMin = values?.dependents_covered?.children_age_limit?.min;
                  const childrenMax = values?.dependents_covered?.children_age_limit?.max;
                  const parentalMin = values?.dependents_covered?.parental_age_limit?.min;
                  const parentalMax = values?.dependents_covered?.parental_age_limit?.max;
                  const sumInsured = values?.sum_insured;
                  const sumInsuredMin = values?.sum_insured_min;
                  const sumInsuredMax = values?.sum_insured_max;
                  
                  // Check if any required field is empty and has an error
                  if ((!maxChildren || maxChildren === '') && errors?.dependents_covered?.max_number_children) return true;
                  if ((!childrenMin || childrenMin === '') && errors?.dependents_covered?.children_age_limit?.min) return true;
                  if ((!childrenMax || childrenMax === '') && errors?.dependents_covered?.children_age_limit?.max) return true;
                  if ((!parentalMin || parentalMin === '') && errors?.dependents_covered?.parental_age_limit?.min) return true;
                  if ((!parentalMax || parentalMax === '') && errors?.dependents_covered?.parental_age_limit?.max) return true;
                  if ((!sumInsured || sumInsured === '') && errors?.sum_insured) return true;
                  if ((!sumInsuredMin || sumInsuredMin === '') && errors?.sum_insured_min) return true;
                  if ((!sumInsuredMax || sumInsuredMax === '') && errors?.sum_insured_max) return true;
                  
                  // Use unified validation function to check toggle fields only if there are active toggles
                  const hasActiveToggles = Object.values(toggleStatus || {}).some(status => status === true);
                  if (hasActiveToggles) {
                    return validateToggleFields(values);
                  }
                  return false;
                };
                
                // Show banner on save attempt when there are validation errors
                const bannerShouldShow = hasRequiredFieldErrors() && formikProps.submitCount > 0;
                console.log("Banner condition check:", {
                  hasRequiredFieldErrors: hasRequiredFieldErrors(),
                  submitCount: formikProps.submitCount,
                  bannerShouldShow,
                  toggleStatus: toggleStatus,
                  hasActiveToggles: Object.values(toggleStatus || {}).some(status => status === true)
                });
                return bannerShouldShow;
              })() && (
                  <div
                    style={{
                      background: "#FFF2CF",
                      border: "1px solid #FBD294",
                      padding: "2rem",
                      borderRadius: "6px",
                      marginTop: "2rem",
                      marginBottom: "2rem",
                      display: "flex",
                      gap: "1rem",
                      alignItems: "flex-start",
                    }}
                  >
                    <img
                      src={Alert}
                      style={{
                        width: "24px",
                      }}
                      alt=""
                    />
                    <div>
                      <Ptag
                        color="#777575"
                        fontSize="12px"
                        marginBottom="0.4rem"
                      >
                        Attention Required:
                      </Ptag>
                      <Ptag>
                        {(() => {
                          // Check if maternity coverage is the issue
                          const isMaternityEnabled = toggleStatus["Maternity Coverage"];
                          if (isMaternityEnabled) {
                            const isMetroTogglerOn = formikProps.values.maternity_metro_toggler;
                            let hasEmptyMaternityFields = false;
                            
                            if (isMetroTogglerOn) {
                              const metroFields = [
                                "metro_maternity_delivery_coverage_limit_normal",
                                "metro_maternity_delivery_coverage_limit_caeserian"
                              ];
                              const nonMetroFields = [
                                "non_metro_maternity_delivery_coverage_limit_normal",
                                "non_metro_maternity_delivery_coverage_limit_caeserian"
                              ];
                              
                              hasEmptyMaternityFields = [...metroFields, ...nonMetroFields].some(field => {
                                const value = formikProps.values[field];
                                return !value || (typeof value === 'string' && value.trim() === '');
                              });
                            } else {
                              const defaultFields = [
                                "maternity_delivery_coverage_limit_normal_max",
                                "maternity_delivery_coverage_limit_icu_max"
                              ];
                              
                              hasEmptyMaternityFields = defaultFields.some(field => {
                                const value = formikProps.values[field];
                                return !value || (typeof value === 'string' && value.trim() === '');
                              });
                            }
                            
                            if (hasEmptyMaternityFields) {
                              return "Maternity Coverage is currently enabled. Please review your maternity coverage settings before proceeding.";
                            }
                          }
                          return "It appears there are mandatory fields that require completion before proceeding. Kindly fill them in to continue.";
                        })()}
                      </Ptag>
                    </div>
                  </div>
                )}
              {getAttribute("Sum Insured") && (
                <GradeSumInsured
                  formikProps={formikProps}
                  isPostIssuance={isPostIssuance}
                  disabled={isPostIssuance && grade.userplan}
                  attribute={getAttribute("Sum Insured")}
                />
              )}
              {[
                "health_insurance",
                "health_insurance_additional",
                "opd_cover",
                "top_up_health",
                "parental_cover",
                "health_insurance_parents",
                "health_insurance_parents_in_laws",
              ].includes(productType) && (
                  <>
                    <DependentsCardList
                      submitCount={formikProps?.submitCount}
                      handleClick={handleClick}
                      selectedDependents={selectedDependents}
                      isEmployeeDefaultPlan={isEmployeeDefaultPlan}
                      isParentsDefaultPlan={isParentsDefaultPlan}
                    />
                    {showChildren && (
                      <div
                        style={{
                          display: "flex",
                          gap: "2rem",
                          minWidth: "800px",
                        }}
                      >
                        <div style={{ width: "100%" }}>
                          <Ptag
                            color={colors.primaryColor}
                            fontSize="14px"
                            marginTop="32px"
                            marginBottom="16px"
                          >
                            Max No. of Children Allowed
                          </Ptag>
                          <BaseDropdown
                            fontSize="16px"
                            options={maxChildrenDropdownOptions(6, true)}
                              formikProps={{
                                ...formikProps,
                                name: "dependents_covered.max_number_children",
                                errors: {
                                  "dependents_covered.max_number_children": formikProps.errors?.dependents_covered?.max_number_children
                                },
                                touched: {
                                  "dependents_covered.max_number_children": formikProps.touched?.dependents_covered?.max_number_children
                                }
                              }}
                          />
                        </div>
                        <div
                          style={{
                            width: "100%",
                          }}
                        >
                          <Ptag
                            color={colors.primaryColor}
                            fontSize="14px"
                            marginTop="32px"
                            marginBottom="16px"
                          >
                            Children Age Limit
                          </Ptag>
                          <div
                            style={{
                              display: "flex",
                              width: "100%",
                              gap: "1rem",
                            }}
                          >
                            <div style={{ width: "100%" }}>
                              <BaseDropdown
                                fontSize="16px"
                                label="Min"
                                options={ageLimitOptions(100, -1)}
                                formikProps={{
                                  ...formikProps,
                                  name: "dependents_covered.children_age_limit.min",
                                  errors: {
                                    "dependents_covered.children_age_limit.min": formikProps.errors?.dependents_covered?.children_age_limit?.min
                                  },
                                  touched: {
                                    "dependents_covered.children_age_limit.min": formikProps.touched?.dependents_covered?.children_age_limit?.min
                                  }
                                }}
                              />
                            </div>
                            <div style={{ width: "100%" }}>
                              <BaseDropdown
                                fontSize="16px"
                                label="Max"
                                options={ageLimitOptions(100, -1)}
                                formikProps={{
                                  ...formikProps,
                                  name: "dependents_covered.children_age_limit.max",
                                  errors: {
                                    "dependents_covered.children_age_limit.max": formikProps.errors?.dependents_covered?.children_age_limit?.max
                                  },
                                  touched: {
                                    "dependents_covered.children_age_limit.max": formikProps.touched?.dependents_covered?.children_age_limit?.max
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {showParents && (
                      <div
                        style={{
                          width: "100%",
                        }}
                      >
                        <Ptag
                          color={colors.primaryColor}
                          fontSize="14px"
                          marginTop="22px"
                          marginBottom="16px"
                        >
                          Parental/In-laws Age Limit
                        </Ptag>
                        <div
                          style={{
                            display: "flex",
                            width: "50%",
                            minWidth: "300px",
                            gap: "1rem",
                          }}
                        >
                          <div style={{ width: "100%" }}>
                            <BaseDropdown
                              fontSize="16px"
                              label="Min Value"
                              options={ageLimitOptions(83, 17)}
                              formikProps={{
                                ...formikProps,
                                name: "dependents_covered.parental_age_limit.min",
                                errors: {
                                  "dependents_covered.parental_age_limit.min": formikProps.errors?.dependents_covered?.parental_age_limit?.min
                                },
                                touched: {
                                  "dependents_covered.parental_age_limit.min": formikProps.touched?.dependents_covered?.parental_age_limit?.min
                                }
                              }}
                            />
                          </div>
                          <div style={{ width: "100%" }}>
                            <BaseDropdown
                              fontSize="16px"
                              label="Max Value"
                              options={ageLimitOptions(83, 17)}
                              formikProps={{
                                ...formikProps,
                                name: "dependents_covered.parental_age_limit.max",
                                errors: {
                                  "dependents_covered.parental_age_limit.max": formikProps.errors?.dependents_covered?.parental_age_limit?.max
                                },
                                touched: {
                                  "dependents_covered.parental_age_limit.max": formikProps.touched?.dependents_covered?.parental_age_limit?.max
                                }
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              {getAttribute("Co Payment") && (
                <GradeCoPayment
                  formikProps={formikProps}
                  attribute={getAttribute("Co Payment")}
                  toggleHandler={toggleHandler}
                  selectedDependents={selectedDependents}
                />
              )}
        {getAttribute("Room Rent Limit") && (
          <GradeRoomLimit
            formikProps={formikProps}
            attribute={getAttribute("Room Rent Limit")}
            toggleHandler={toggleHandler}
          />
        )}
              {getAttribute("Maternity Coverage") && (
                <GradeMaternity
                  formikProps={formikProps}
                  attribute={getAttribute("Maternity Coverage")}
                  toggleHandler={toggleHandler}
                  onToggleChange={handleMaternityToggleChange}
                />
              )}
              {getAttribute("Ambulance Charges") && (
                <GradeDynamic
                  toggleHandler={toggleHandler}
                  formikProps={formikProps}
                  title="Ambulance Charges"
                  attribute={getAttribute("Ambulance Charges")}
                  formikKey="ambulance"
                />
              )}
              {getAttribute("OPD Charges") && (
                <GradeDynamic
                  toggleHandler={toggleHandler}
                  formikProps={formikProps}
                  title="OPD Charges"
                  attribute={getAttribute("OPD Charges")}
                  formikKey="opd"
                />
              )}
              {getAttribute("Health Checkup Coverage") && (
                <GradeDynamic
                  toggleHandler={toggleHandler}
                  formikProps={formikProps}
                  title="Health Checkup Coverage"
                  attribute={getAttribute("Health Checkup Coverage")}
                  formikKey="health_checkup"
                />
              )}
              {getAttribute("Dental Coverage") && (
                <GradeDynamic
                  toggleHandler={toggleHandler}
                  formikProps={formikProps}
                  title="Dental Coverage"
                  attribute={getAttribute("Dental Coverage")}
                  formikKey="dental"
                />
              )}
              {getAttribute("Disease Wise Sub limit") && (
                <GradeDiseaseWise
                  formikProps={formikProps}
                  title="Disease Wise Sub limit"
                  attribute={getAttribute("Disease Wise Sub limit")}
                  toggleHandler={toggleHandler}
                />
              )}
              {getAttribute("Critical Illness Coverages") && (
                <GradeDynamic
                  toggleHandler={toggleHandler}
                  formikProps={formikProps}
                  title="Critical Illness Coverages"
                  attribute={getAttribute("Critical Illness Coverages")}
                  formikKey="critical_illness"
                />
              )}
              {getAttribute("AYUSH Treatment") && (
                <GradeDynamic
                  toggleHandler={toggleHandler}
                  formikProps={formikProps}
                  title="AYUSH Treatment"
                  attribute={getAttribute("AYUSH Treatment")}
                  formikKey="ayush_treatment"
                />
              )}
              {getAttribute("Congenital Internal Disease Coverage") && (
                <GradeDynamic
                  toggleHandler={toggleHandler}
                  formikProps={formikProps}
                  title="Congenital Internal Disease Coverage"
                  attribute={getAttribute(
                    "Congenital Internal Disease Coverage"
                  )}
                  formikKey="congenital_internal"
                />
              )}
              {getAttribute("Congenital External Disease Coverage") && (
                <GradeDynamic
                  toggleHandler={toggleHandler}
                  formikProps={formikProps}
                  title="Congenital External Disease Coverage"
                  attribute={getAttribute(
                    "Congenital External Disease Coverage"
                  )}
                  formikKey="congenital_external"
                />
              )}

              <GradeAccordion
                title="Coverage Notes (optional)"
                hideSwitch
                canSelect
                expanded
              >
                <FieldArray
                  name="other_details"
                  render={(arrayHelpers) => (
                    <div>
                      {(formikProps.values.other_details && formikProps.values.other_details.length > 0 ? formikProps.values.other_details : [""]).map((_, index) => (
                          <div
                            key={index}
                            style={{
                              marginBottom: "0.4rem",
                            }}
                          >
                            <BaseTextArea name={`other_details.${index}`} />
                          </div>
                        ))}
                      <footer
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        {(formikProps.values.other_details && formikProps.values.other_details.length > 1) && (
                          <Button
                            sx={{
                              fontSize: "14px !important",
                              textDecoration: "underline",
                              textTransform: "capitalize",
                              color: "#DE6944",
                              alignItems: 'center',
                              "& .MuiButton-startIcon": {
                                marginRight: 0.25,
                              }
                            }}
                            startIcon={<RemoveCircleOutlineIcon />}
                            onClick={() =>
                              arrayHelpers.remove(
                                (formikProps.values.other_details && formikProps.values.other_details.length > 0 ? formikProps.values.other_details.length : 1) - 1
                              )
                            }
                          >
                            <Ptag color="#DE6944" fontSize={'14px'}>
                              Remove
                            </Ptag>

                          </Button>
                        )}
                        <Button
                          sx={{
                            fontSize: "14px !important",
                            textDecoration: "underline",
                            textTransform: "capitalize",
                            color: colors.primaryColor,
                            alignItems: 'center',
                            "& .MuiButton-startIcon": {
                              marginRight: 0.25,
                            }
                          }}
                          startIcon={<ControlPointIcon />}
                          onClick={() => arrayHelpers.push("")}
                        >
                          <Ptag fontSize={'14px'}>
                            Add
                          </Ptag>

                        </Button>
                      </footer>
                    </div>
                  )}
                />
              </GradeAccordion>
              <footer className="footer-btn">
                <div>
                  {/* <LargeButton
                    style={{
                      backgroundColor: "transparent",
                      border: `1px solid ${colors.primaryColor}`,
                      color: colors.primaryColor,
                      padding: "0px",
                    }}
                    label="Delete"
                  /> */}
                  {!isFlat && (
                    <LargeButton
                      style={{
                        width: "200px",
                        fontSize: "16px",
                        fontWeight: "500",
                        padding: "16px 20px",
                      }}
                      bgColor="white"
                      hoverBgColor={"white"}
                      borderColor={colors.primaryColor}
                      textColor={colors.primaryColor}
                      onClick={(event) =>
                        handleDuplicateGradeClick(event, formikProps.values)
                      }
                      label={`Duplicate Grade`}
                    />
                  )}
                  <LargeButton
                    style={{
                      width: "200px",
                      fontSize: "16px",
                      fontWeight: "500",
                      padding: "16px 20px",
                    }}
                    type="submit"
                    onClick={() => {
                      // Simple scroll to top on save click
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    label={`Save ${!isFlat ? "Grade" : ""}`}
                  />
                </div>
              </footer>
            </Form>
          );
        }}
      </Formik>
      </section>
    </>
  );
};

export default GradeView;
